// ---------------------------------------------------------------------------
// Test Mode — simulated RBAC matrix (CLE-252).
//
// The demo account is a full-access ADMIN. `GET /api/me/permissions` must return
// a coherent, fully-allowed matrix so every permission-gated section (dashboard
// cards, sidebar entries, create buttons, Settings, AI) renders. Mirrors the
// backend `fullAccessMatrix()` (backend/src/lib/permissions/templates.ts) so the
// simulated session behaves exactly like a real admin.
//
// Previously this endpoint was NOT simulated: the mock router explicitly threw a
// TEST_MODE_UNHANDLED error for any `/permissions` path, which spammed the
// console with failed requests and broke RBAC for any non-admin consumer.
// ---------------------------------------------------------------------------

import type { UserPermissionMatrix, RoleTemplate } from '../lib/permissionsApi';
import { isoDateTime } from './company';

// Local deny-all matrix — mirrors permissionsApi.denyAll() but avoids a
// circular import (api.ts → mockRouter → testData → permissionsApi → api.ts).
function denyAll(): UserPermissionMatrix {
  return {
    projects: {
      canView: false,
      canCreate: false,
      canEdit: false,
      canDelete: false,
      scope: 'assigned',
      canViewOverview: false,
      canViewFinancials: false,
      canEditFinancials: false,
      canViewTeam: false,
      canEditTeam: false,
      canViewResources: false,
      canEditResources: false,
      canViewMeetingNotes: false,
      canEditMeetingNotes: false,
      canViewVault: false,
      canRevealVault: false,
      canEditVault: false,
    },
    tasks: {
      canView: false,
      canCreate: false,
      canEdit: false,
      canDelete: false,
      canBulkEdit: false,
      canViewReports: false,
      scope: 'assigned',
    },
    leads: {
      canView: false,
      canCreate: false,
      canEdit: false,
      canDelete: false,
      canConvert: false,
      canViewActions: false,
      canEditActions: false,
      canConfigurePipeline: false,
    },
    clients: {
      canView: false,
      canCreate: false,
      canEdit: false,
      canDelete: false,
      canViewContacts: false,
      canEditContacts: false,
      canViewResources: false,
      canEditResources: false,
      canViewMeetingNotes: false,
      canEditMeetingNotes: false,
    },
    meetings: {
      canView: false,
      canCreate: false,
      canEdit: false,
      canDelete: false,
      scope: 'involved',
      canViewNotes: false,
      canEditNotes: false,
      canManageContacts: false,
    },
    videoMeetings: { canView: false, canCreate: false, canManage: false },
    socialPosts: {
      canView: false,
      canCreate: false,
      canEdit: false,
      canDelete: false,
      canManageShareLinks: false,
    },
    suppliers: { canView: false, canCreate: false, canEdit: false, canDelete: false },
    actors: { canView: false, canCreate: false, canEdit: false, canDelete: false },
    whatsapp: { canView: false, canConfigure: false },
    subscriptions: { canView: false, canCreate: false, canEdit: false, canDelete: false },
    financials: {
      canView: false,
      canViewPnL: false,
      canViewInvoices: false,
      canCreateInvoices: false,
      canEditInvoices: false,
      canDeleteInvoices: false,
      canViewSmartBill: false,
      canSyncSmartBill: false,
      canAssociateSmartBill: false,
      canViewSpv: false,
      canSyncSpv: false,
      canIssueInvoices: false,
      canApproveInvoices: false,
      canViewBankStatements: false,
      canUploadBankStatements: false,
      canReconcile: false,
      canExportReports: false,
      canSendReminders: false,
      canManageScheduledPayments: false,
    },
    people: {
      canView: false,
      canCreate: false,
      canEdit: false,
      canDelete: false,
      canViewCapacity: false,
      canEditAllocations: false,
      canViewRates: false,
      canManageRoles: false,
    },
    dailySummary: { canView: false, scope: 'self', canExportPdf: false, canSendEmail: false },
    weeklySummary: { canView: false, scope: 'self', canExport: false },
    aiAssistant: {
      canAccess: false,
      canCreateTasks: false,
      canCreateLeads: false,
      canIngestDocuments: false,
      canQueryFinancials: false,
      canWriteKb: false,
      modelAccess: 'haiku',
    },
    settings: {
      canAccessSettings: false,
      canManageCompanies: false,
      canEditIntegrations: false,
      canEditExpenseCategories: false,
      canEditLeadPipelines: false,
      canEditDepartments: false,
      canEditServices: false,
      canEditTaskStatuses: false,
      canEditTaskTemplates: false,
      canEditEmailTemplates: false,
      canManageUsers: false,
      canManageRoles: false,
    },
    leave: {
      canViewAll: false,
      canApprove: false,
      canManage: false,
    },
    dashboard: {
      canView: false,
      canViewFinancialCards: false,
      canViewTeamAllocation: false,
      canViewActivityFeed: false,
    },
  };
}

/** Full-access matrix for the demo admin — mirrors backend fullAccessMatrix(). */
export function buildPermissionMatrix(): UserPermissionMatrix {
  return {
    projects: {
      canView: true,
      canCreate: true,
      canEdit: true,
      canDelete: true,
      scope: 'all',
      canViewOverview: true,
      canViewFinancials: true,
      canEditFinancials: true,
      canViewTeam: true,
      canEditTeam: true,
      canViewResources: true,
      canEditResources: true,
      canViewMeetingNotes: true,
      canEditMeetingNotes: true,
      canViewVault: true,
      canRevealVault: true,
      canEditVault: true,
    },
    tasks: {
      canView: true,
      canCreate: true,
      canEdit: true,
      canDelete: true,
      canBulkEdit: true,
      canViewReports: true,
      scope: 'all',
    },
    leads: {
      canView: true,
      canCreate: true,
      canEdit: true,
      canDelete: true,
      canConvert: true,
      canViewActions: true,
      canEditActions: true,
      canConfigurePipeline: true,
    },
    clients: {
      canView: true,
      canCreate: true,
      canEdit: true,
      canDelete: true,
      canViewContacts: true,
      canEditContacts: true,
      canViewResources: true,
      canEditResources: true,
      canViewMeetingNotes: true,
      canEditMeetingNotes: true,
    },
    meetings: {
      canView: true,
      canCreate: true,
      canEdit: true,
      canDelete: true,
      scope: 'all',
      canViewNotes: true,
      canEditNotes: true,
      canManageContacts: true,
    },
    videoMeetings: { canView: true, canCreate: true, canManage: true },
    socialPosts: {
      canView: true,
      canCreate: true,
      canEdit: true,
      canDelete: true,
      canManageShareLinks: true,
    },
    suppliers: {
      canView: true,
      canCreate: true,
      canEdit: true,
      canDelete: true,
    },
    actors: {
      canView: true,
      canCreate: true,
      canEdit: true,
      canDelete: true,
    },
    whatsapp: { canView: true, canConfigure: true },
    subscriptions: {
      canView: true,
      canCreate: true,
      canEdit: true,
      canDelete: true,
    },
    financials: {
      canView: true,
      canViewPnL: true,
      canViewInvoices: true,
      canCreateInvoices: true,
      canEditInvoices: true,
      canDeleteInvoices: true,
      canViewSmartBill: true,
      canSyncSmartBill: true,
      canAssociateSmartBill: true,
      canViewSpv: true,
      canSyncSpv: true,
      canIssueInvoices: true,
      canApproveInvoices: true,
      canViewBankStatements: true,
      canUploadBankStatements: true,
      canReconcile: true,
      canExportReports: true,
      canSendReminders: true,
      canManageScheduledPayments: true,
    },
    people: {
      canView: true,
      canCreate: true,
      canEdit: true,
      canDelete: true,
      canViewCapacity: true,
      canEditAllocations: true,
      canViewRates: true,
      canManageRoles: true,
    },
    dailySummary: {
      canView: true,
      scope: 'all',
      canExportPdf: true,
      canSendEmail: true,
    },
    weeklySummary: {
      canView: true,
      scope: 'all',
      canExport: true,
    },
    aiAssistant: {
      canAccess: true,
      canCreateTasks: true,
      canCreateLeads: true,
      canIngestDocuments: true,
      canQueryFinancials: true,
      canWriteKb: true,
      modelAccess: 'both',
    },
    settings: {
      canAccessSettings: true,
      canManageCompanies: true,
      canEditIntegrations: true,
      canEditExpenseCategories: true,
      canEditLeadPipelines: true,
      canEditDepartments: true,
      canEditServices: true,
      canEditTaskStatuses: true,
      canEditTaskTemplates: true,
      canEditEmailTemplates: true,
      canManageUsers: true,
      canManageRoles: true,
    },
    leave: {
      canViewAll: true,
      canApprove: true,
      canManage: true,
    },
    dashboard: {
      canView: true,
      canViewFinancialCards: true,
      canViewTeamAllocation: true,
      canViewActivityFeed: true,
    },
  };
}

/** Simulated role templates for Settings → Roles & Permissions. */
export function buildRoleTemplates(): RoleTemplate[] {
  const adminPerms = buildPermissionMatrix();
  const memberPerms: UserPermissionMatrix = {
    ...denyAll(),
    projects: {
      ...denyAll().projects,
      canView: true,
      scope: 'assigned',
      canViewOverview: true,
      canViewTeam: true,
      canViewResources: true,
      canViewMeetingNotes: true,
    },
    tasks: { ...denyAll().tasks, canView: true, canCreate: true, canEdit: true, scope: 'assigned' },
    leads: { ...denyAll().leads, canView: true },
    clients: { ...denyAll().clients, canView: true, canViewContacts: true },
    meetings: { ...denyAll().meetings, canView: true, canCreate: true, canViewNotes: true },
    suppliers: { ...denyAll().suppliers, canView: true },
    actors: { ...denyAll().actors, canView: true },
    subscriptions: { ...denyAll().subscriptions, canView: true },
    financials: { ...denyAll().financials, canView: true, canViewInvoices: true },
    people: { ...denyAll().people, canView: true },
    dailySummary: { canView: true, scope: 'self', canExportPdf: false, canSendEmail: false },
    weeklySummary: { canView: true, scope: 'self', canExport: false },
    aiAssistant: { ...denyAll().aiAssistant, canAccess: true, modelAccess: 'haiku' },
    dashboard: {
      canView: true,
      canViewFinancialCards: false,
      canViewTeamAllocation: false,
      canViewActivityFeed: true,
    },
  };
  const viewerPerms: UserPermissionMatrix = {
    ...denyAll(),
    projects: { ...denyAll().projects, canView: true, scope: 'all' },
    tasks: { ...denyAll().tasks, canView: true, scope: 'all' },
    clients: { ...denyAll().clients, canView: true },
    dashboard: {
      canView: true,
      canViewFinancialCards: false,
      canViewTeamAllocation: false,
      canViewActivityFeed: true,
    },
  };

  return [
    {
      id: 'role-admin',
      name: 'Administrator',
      description: 'Acces complet la toate funcționalitățile',
      permissions: adminPerms,
      isSystem: true,
      createdAt: isoDateTime(-540, 9),
      updatedAt: isoDateTime(-540, 9),
    },
    {
      id: 'role-member',
      name: 'Membru echipă',
      description: 'Acces la proiecte asignate, sarcini, și funcții de bază',
      permissions: memberPerms,
      isSystem: true,
      createdAt: isoDateTime(-540, 9),
      updatedAt: isoDateTime(-540, 9),
    },
    {
      id: 'role-viewer',
      name: 'Vizualizator',
      description: 'Acces read-only la proiecte și sarcini',
      permissions: viewerPerms,
      isSystem: true,
      createdAt: isoDateTime(-540, 9),
      updatedAt: isoDateTime(-540, 9),
    },
  ];
}
